import type { ReactNode } from 'react';

import { Box, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface SwitchFieldProps {
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /**
   * Explicit accessible name, for when `label` is rich content (e.g. a channel-scope chip/action
   * rendered alongside the text) rather than a plain string the control can read directly.
   */
  ariaLabel?: string;
}

/** Boolean toggle laid out as a settings row (label left, switch right). */
export function SwitchField({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  ariaLabel,
}: SwitchFieldProps) {
  const { t } = useTranslation('settings');
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        slotProps={{
          input: {
            'aria-label':
              ariaLabel ??
              (typeof label === 'string' ? label : t('switchField.toggleFallbackLabel')),
          },
        }}
      />
    </Box>
  );
}
